import React, { useRef, useEffect, useState } from 'react';
import { useCanvas } from '../context/CanvasContext';
import BookmarkBlock from './BookmarkBlock';
import TextBlock from './TextBlock';
import { motion, useMotionValue } from 'framer-motion';

const Canvas = () => {
  const { 
    blocks, 
    mode, 
    zoom, 
    setZoom, 
    updateCanvasPosition, 
    setSelectedBlockId, 
    selectedBlockId,
    undo,
    removeBlock,
    copyBlock,
    pasteBlock,
    initialView,
    saveCanvasPosition
  } = useCanvas();
  const canvasRef = useRef(null);
  const [isPanning, setIsPanning] = useState(false);
  
  const x = useMotionValue(initialView.x);
  const y = useMotionValue(initialView.y);

  useEffect(() => {
    const unsubscribeX = x.on("change", (latestX) => {
      updateCanvasPosition(latestX, y.get());
    });
    const unsubscribeY = y.on("change", (latestY) => {
      updateCanvasPosition(x.get(), latestY);
    });
    return () => {
      unsubscribeX();
      unsubscribeY();
    };
  }, [x, y, updateCanvasPosition]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ignore if typing in an input or textarea
      if (['INPUT', 'TEXTAREA'].includes(document.activeElement.tagName)) {
        return;
      }

      if ((e.metaKey || e.ctrlKey) && e.key === 'z') {
        e.preventDefault();
        undo();
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 'c') {
        e.preventDefault();
        copyBlock();
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 'v') {
        e.preventDefault();
        pasteBlock();
      }
      if (e.key === 'Backspace' || e.key === 'Delete') {
        if (selectedBlockId) {
            e.preventDefault();
            removeBlock(selectedBlockId);
            setSelectedBlockId(null);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [undo, copyBlock, pasteBlock, removeBlock, selectedBlockId, setSelectedBlockId]);

  useEffect(() => {
    const handleWheel = (e) => {
      if (e.ctrlKey || e.metaKey || !isPanning) {
        e.preventDefault();
        const delta = -e.deltaY * 0.001;
        setZoom(prev => Math.min(Math.max(prev + delta, 0.1), 3));
      }
    };

    const canvas = canvasRef.current;
    if (canvas) {
      canvas.addEventListener('wheel', handleWheel, { passive: false });
    }

    return () => {
      if (canvas) {
        canvas.removeEventListener('wheel', handleWheel);
      }
    };
  }, [setZoom, isPanning]);

  const hasPannedRef = useRef(false);

  useEffect(() => {
    const handlePointerMove = (e) => {
      if (isPanning) {
        // Calculate total movement to detect drag vs click
        const movement = Math.sqrt(e.movementX ** 2 + e.movementY ** 2);
        if (movement > 0) {
           hasPannedRef.current = true;
        }

        const newX = x.get() + e.movementX;
        const newY = y.get() + e.movementY;
        // Limit panning to avoid getting lost (e.g., +/- 1500px)
        // Adjust limit based on zoom to keep reachable world area constant
        const BASE_LIMIT = 25000;
        const limit = BASE_LIMIT * zoom;
        
        x.set(Math.max(-limit, Math.min(limit, newX)));
        y.set(Math.max(-limit, Math.min(limit, newY)));
      }
    };

    const handlePointerUp = () => {
      if (isPanning) {
        saveCanvasPosition(x.get(), y.get());
      }
      setIsPanning(false);
    };

    if (isPanning) {
      window.addEventListener('pointermove', handlePointerMove);
      window.addEventListener('pointerup', handlePointerUp);
    }

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, [isPanning, x, y, zoom]);

  const handlePointerDown = (e) => {
    hasPannedRef.current = false;
    
    // Only deselect if clicking directly on the canvas background, not on a block
    if (e.target.id === 'canvas-root' || e.target.id === 'canvas-bg' || e.target === canvasRef.current) {
        setSelectedBlockId(null);
    }

    // Allow panning if:
    // 1. It's on background
    // 2. OR mode is 'view' (allows dragging anywhere to pan)
    // 3. OR middle click / modifier keys are used
    const isBackground = e.target === canvasRef.current || e.target.id === 'canvas-bg' || e.target.id === 'canvas-root';
    const shouldPan = isBackground || mode === 'view' || e.button === 1 || e.ctrlKey || e.metaKey;

    if (shouldPan) {
        setIsPanning(true);
        // Important: capture pointer so we keep getting events even if mouse leaves target
        // But be careful not to break interactions in edit mode if not background
        if (mode === 'view' || isBackground) {
             // e.target.setPointerCapture(e.pointerId); // Can cause issues with click bubbling if not careful
        }
    }
  };

  const handleClickCapture = (e) => {
      // If we dragged/panned, prevent the click from triggering links or other actions
      if (hasPannedRef.current && (mode === 'view' || e.button === 1 || e.ctrlKey || e.metaKey)) {
          e.stopPropagation();
          e.preventDefault(); // Prevent link navigation
          hasPannedRef.current = false;
      }
  };

  const handleWheel = (e) => {
    e.preventDefault();
    if (e.ctrlKey || e.metaKey) {
        // Pinch zoom or Ctrl+Wheel
        const newZoom = zoom - e.deltaY * 0.01;
        // useCanvas setZoom handles clamping if I export it, or I update it here via update function
        // I need to check if setZoom is available from useCanvas
    } else {
         // If no modifier, maybe just pan? Or zoom? 
         // User asked to zoom using mouse wheel.
         const newZoom = zoom - e.deltaY * 0.001;
         // But usually wheel is too fast, need to dampen.
    }
  };


  return (
    <div
      ref={canvasRef}
      onPointerDown={handlePointerDown}
      onClickCapture={handleClickCapture}
      style={{
        width: '100vw',
        height: '100vh',
        overflow: 'hidden',
        cursor: isPanning ? 'grabbing' : mode === 'view' ? 'grab' : 'default',
        backgroundColor: 'var(--bg-color)',
        position: 'relative',
        touchAction: 'none',
      }}
    >
      <motion.div 
        id="canvas-bg"
        style={{ 
            position: 'absolute', 
            width: '10000%', 
            height: '10000%', 
            left: '-5000%',
            top: '-5000%',
            zIndex: 0,
            backgroundImage: 'radial-gradient(var(--border-color) 1px, transparent 1px)',
            backgroundSize: '20px 20px',
            opacity: 0.5,
            x,
            y,
            // scale: zoom, // Keep background pattern constant size
            originX: 0.5,
            originY: 0.5
        }} 
      />

      <motion.div
        id="canvas-root"
        style={{
          x,
          y,
          scale: zoom,
          width: '100%',
          height: '100%',
          position: 'absolute',
          top: 0,
          left: 0,
          originX: 0,
          originY: 0,
        }}
      >
        {blocks.map((block) => {
          if (block.type === 'bookmark') {
            return <BookmarkBlock key={block.id} block={block} />;
          }
          if (block.type === 'text') {
            return <TextBlock key={block.id} block={block} />;
          }
          return null;
        })}
      </motion.div>
    </div>
  );
};

export default Canvas;
